import React, { useState, useEffect, useRef } from 'react';
import {
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  message,
  Popconfirm,
  Card,
  Typography,
  Switch,
  ColorPicker,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  InboxOutlined,
  LockOutlined,
  UserOutlined,
  PhoneOutlined,
} from '@ant-design/icons';
import { User, UserRole, UserStatus } from '../../types';
import * as authApi from '../../services/authApi';

const { Title } = Typography;
const { Option } = Select;

// Default swatches offered for a user's scheduling calendar block color.
// Custom colors beyond these are available via the AntD ColorPicker.
const DEFAULT_CALENDAR_SWATCHES = [
  '#4f8cff', // blue
  '#f97316', // orange
  '#22c55e', // green
  '#a855f7', // purple
  '#ef4444', // red
  '#eab308', // yellow
];

// Small controlled control matching AntD Form.Item's value/onChange contract
// so it can be used directly as a Form.Item child.
const CalendarColorPicker: React.FC<{
  value?: string | null;
  onChange?: (value: string) => void;
}> = ({ value, onChange }) => (
  <Space wrap>
    {DEFAULT_CALENDAR_SWATCHES.map((swatch) => (
      <Button
        key={swatch}
        shape="circle"
        size="small"
        aria-label={`swatch-${swatch}`}
        onClick={() => onChange?.(swatch)}
        style={{
          backgroundColor: swatch,
          border: value === swatch ? '2px solid #1f1f1f' : '1px solid #d9d9d9',
          padding: 0,
        }}
      />
    ))}
    <ColorPicker
      value={value || undefined}
      onChange={(color) => onChange?.(color.toHexString())}
    />
  </Space>
);

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [form] = Form.useForm();
  const [resetForm] = Form.useForm();
  // True only once the user's profile (incl. calendar_color) has been
  // successfully fetched for the user currently open in the edit modal.
  // Guards handleSubmit: if the getUserById preload failed, we must NOT
  // send calendar_color to updateUserProfile — the form field is at its
  // reset/undefined value, and `calendar_color ?? null` would silently
  // wipe the user's previously-saved color even though nobody touched it.
  const profileLoadedRef = useRef(false);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const data = await authApi.getAllUsers();
      setUsers(data);
    } catch (error: any) {
      message.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = () => {
    setEditingUser(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEditUser = async (user: User) => {
    setEditingUser(user);
    profileLoadedRef.current = false;
    form.setFieldsValue({
      email: user.email,
      first_name: user.first_name,
      last_name: user.last_name,
      phone: user.phone,
      role: user.role,
      status: user.status,
      calendar_color: undefined,
      manage_diagnosis: false,
      manage_schedule: false,
      override_booking_conflict: false,
      edit_prices: false,
      apply_discounts: false,
      edit_customers: false,
      refund_payments: false,
      bypass_inspection: false,
    });
    setModalVisible(true);
    // Load the user's profile to reflect current capability flags in the toggle.
    try {
      const full = await authApi.getUserById(user.id);
      const perms = full.profile?.permissions || [];
      form.setFieldsValue({
        calendar_color: full.profile?.calendar_color || undefined,
        manage_diagnosis: perms.includes('manage_diagnosis'),
        manage_schedule: perms.includes('manage_schedule'),
        override_booking_conflict: perms.includes('override_booking_conflict'),
        edit_prices: perms.includes('edit_prices'),
        apply_discounts: perms.includes('apply_discounts'),
        edit_customers: perms.includes('edit_customers'),
        refund_payments: perms.includes('refund_payments'),
        bypass_inspection: perms.includes('bypass_inspection'),
      });
      // Only mark the profile as loaded once we've actually populated the
      // form from the server — a failed fetch below must leave this false.
      profileLoadedRef.current = true;
    } catch {
      // Non-fatal: leave the toggle at its default (off). Also leave
      // profileLoadedRef false so handleSubmit skips the profile write
      // instead of clearing the user's stored calendar_color.
    }
  };

  const handleArchiveUser = async (userId: number) => {
    try {
      // Endpoint soft-archives (sets status='inactive') — it does not remove
      // the user. History is preserved and the user still appears in this
      // list, tagged INACTIVE.
      await authApi.deleteUser(userId);
      message.success('User archived');
      loadUsers();
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to archive user');
    }
  };

  const handleResetPassword = async (userId: number) => {
    resetForm.resetFields();
    Modal.confirm({
      title: 'Reset Password',
      content: (
        <Form form={resetForm} layout="vertical">
          <Form.Item
            name="newPassword"
            label="New Password"
            rules={[
              { required: true, message: 'Please enter new password' },
              { min: 8, message: 'Password must be at least 8 characters' },
            ]}
          >
            <Input.Password />
          </Form.Item>
        </Form>
      ),
      okText: 'Reset Password',
      // Return a Promise so the modal stays open if validation fails or the
      // request errors — otherwise it would close silently with no reset.
      onOk: async () => {
        const values = await resetForm.validateFields();
        try {
          await authApi.resetUserPassword(userId, values.newPassword);
          message.success('Password reset successfully');
        } catch (error: any) {
          message.error(error.response?.data?.error || 'Failed to reset password');
          throw error; // keep the modal open on server error
        }
      },
    });
  };

  const handleSubmit = async (values: any) => {
    try {
      // Translate capability toggles into a permissions array.
      const {
        manage_diagnosis,
        manage_schedule,
        override_booking_conflict,
        edit_prices,
        apply_discounts,
        edit_customers,
        refund_payments,
        bypass_inspection,
        calendar_color,
        ...rest
      } = values;
      const payload: any = { ...rest };
      if (editingUser) {
        const caps: string[] = [];
        if (manage_diagnosis) caps.push('manage_diagnosis');
        if (manage_schedule) caps.push('manage_schedule');
        if (override_booking_conflict) caps.push('override_booking_conflict');
        if (edit_prices) caps.push('edit_prices');
        if (apply_discounts) caps.push('apply_discounts');
        if (edit_customers) caps.push('edit_customers');
        if (refund_payments) caps.push('refund_payments');
        if (bypass_inspection) caps.push('bypass_inspection');
        payload.permissions = caps;
        // Update existing user. If this throws, bail out entirely — nothing
        // persisted, so the generic error + modal-stays-open behavior below
        // is correct.
        await authApi.updateUser(editingUser.id, payload);

        // calendar_color is a user_profiles field served by the profile route
        // (Task 6), not the users-table PUT /users/:id route above. Only
        // send it if the profile actually loaded on edit-open — if the
        // getUserById preload failed, the field is at its reset/undefined
        // value and writing it would silently wipe the user's stored color.
        if (profileLoadedRef.current) {
          try {
            await authApi.updateUserProfile(editingUser.id, {
              calendar_color: calendar_color ?? null,
            });
            message.success('User updated successfully');
          } catch (profileError: any) {
            // The role/permissions change above already persisted — don't
            // claim the whole save failed, and still refresh the list so
            // the table reflects what DID save.
            message.warning('User saved, but the calendar color could not be updated.');
          }
        } else {
          message.success('User updated successfully');
        }

        setModalVisible(false);
        loadUsers();
      } else {
        // Create new user (values already contains password)
        await authApi.createUser(values);
        message.success('User created successfully');
        setModalVisible(false);
        loadUsers();
      }
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to save user');
    }
  };

  const getRoleColor = (role: UserRole): string => {
    const colors: Record<UserRole, string> = {
      owner: 'red',
      advisor: 'blue',
      technician: 'green',
      customer: 'default',
    };
    return colors[role];
  };

  const getStatusColor = (status: UserStatus): string => {
    const colors: Record<UserStatus, string> = {
      active: 'success',
      inactive: 'default',
      suspended: 'error',
    };
    return colors[status];
  };

  const columns = [
    {
      title: 'Name',
      key: 'name',
      render: (record: User) => `${record.first_name} ${record.last_name}`,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (role: UserRole) => (
        <Tag color={getRoleColor(role)}>{role.toUpperCase()}</Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: UserStatus) => (
        <Tag color={getStatusColor(status)}>{status.toUpperCase()}</Tag>
      ),
    },
    {
      title: 'Last Login',
      dataIndex: 'last_login',
      key: 'last_login',
      render: (date: string) => (date ? new Date(date).toLocaleString() : 'Never'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: User) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEditUser(record)}
          >
            Edit
          </Button>
          <Button
            icon={<LockOutlined />}
            size="small"
            onClick={() => handleResetPassword(record.id)}
          >
            Reset Password
          </Button>
          <Popconfirm
            title="Archive this employee?"
            description="Their history is kept; they'll stop appearing in scheduling."
            onConfirm={() => handleArchiveUser(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button icon={<InboxOutlined />} size="small" danger>
              Archive
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={3}>User Management</Title>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreateUser}
          >
            Create User
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={users}
          loading={loading}
          rowKey="id"
          pagination={{ pageSize: 10 }}
          scroll={{ x: 'max-content' }}
        />

        <Modal
          title={editingUser ? 'Edit User' : 'Create New User'}
          open={modalVisible}
          onCancel={() => setModalVisible(false)}
          onOk={() => form.submit()}
          width={600}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
          >
            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, message: 'Please enter email' },
                { type: 'email', message: 'Please enter a valid email' },
              ]}
            >
              <Input prefix={<UserOutlined />} placeholder="user@example.com" />
            </Form.Item>

            <Form.Item
              name="first_name"
              label="First Name"
              rules={[{ required: true, message: 'Please enter first name' }]}
            >
              <Input placeholder="John" />
            </Form.Item>

            <Form.Item
              name="last_name"
              label="Last Name"
              rules={[{ required: true, message: 'Please enter last name' }]}
            >
              <Input placeholder="Doe" />
            </Form.Item>

            <Form.Item
              name="phone"
              label="Phone Number (for SMS Notifications)"
              tooltip="Enter phone number in E.164 format for SMS notifications (e.g., +1234567890)"
              rules={[
                {
                  pattern: /^\+?[1-9]\d{1,14}$/,
                  message: 'Please enter a valid phone number (E.164 format recommended: +1234567890)',
                },
              ]}
            >
              <Input
                prefix={<PhoneOutlined />}
                placeholder="+1234567890"
                addonBefore="📱"
              />
            </Form.Item>

            <Form.Item
              name="role"
              label="Role"
              rules={[{ required: true, message: 'Please select role' }]}
            >
              <Select placeholder="Select role">
                <Option value="owner">Owner</Option>
                <Option value="advisor">Advisor</Option>
                <Option value="technician">Technician</Option>
                <Option value="customer">Customer</Option>
              </Select>
            </Form.Item>

            {editingUser && (
              <Form.Item
                name="status"
                label="Status"
                rules={[{ required: true, message: 'Please select status' }]}
              >
                <Select placeholder="Select status">
                  <Option value="active">Active</Option>
                  <Option value="inactive">Inactive</Option>
                  <Option value="suspended">Suspended</Option>
                </Select>
              </Form.Item>
            )}

            {editingUser && (
              <Form.Item
                name="calendar_color"
                label="Calendar block color"
                tooltip="Color used for this person's blocks on the scheduling calendar."
              >
                <CalendarColorPicker />
              </Form.Item>
            )}

            {editingUser && (
              <Form.Item
                name="manage_diagnosis"
                label="Can manage diagnosis"
                valuePropName="checked"
                tooltip="Allows changing work order type and creating repair orders from a diagnosis."
              >
                <Switch />
              </Form.Item>
            )}

            {editingUser && (
              <Form.Item
                name="manage_schedule"
                label="Can manage scheduling"
                valuePropName="checked"
                tooltip="Allows creating, editing, and cancelling appointments on the schedule."
              >
                <Switch />
              </Form.Item>
            )}

            {editingUser && (
              <Form.Item
                name="override_booking_conflict"
                label="Can override double-booking"
                valuePropName="checked"
                tooltip="Allows booking appointments that conflict with existing slots."
              >
                <Switch />
              </Form.Item>
            )}

            {editingUser && (
              <Form.Item
                name="edit_prices"
                label="Can edit prices"
                valuePropName="checked"
                tooltip="Allows editing labor rates and part unit prices on estimates and work orders."
              >
                <Switch />
              </Form.Item>
            )}

            {editingUser && (
              <Form.Item
                name="apply_discounts"
                label="Can apply discounts/promos"
                valuePropName="checked"
                tooltip="Allows applying discounts and promotions to jobs."
              >
                <Switch />
              </Form.Item>
            )}

            {editingUser && (
              <Form.Item
                name="edit_customers"
                label="Can edit/delete customers"
                valuePropName="checked"
                tooltip="Allows editing and deleting customer records."
              >
                <Switch />
              </Form.Item>
            )}

            {editingUser && (
              <Form.Item
                name="refund_payments"
                label="Can refund/void payments"
                valuePropName="checked"
                tooltip="Allows refunding and voiding payments."
              >
                <Switch />
              </Form.Item>
            )}

            {editingUser && (
              <Form.Item
                name="bypass_inspection"
                label="Bypass inspection completion"
                valuePropName="checked"
                tooltip="Allows completing a work order even when required (non-mandatory) DVI inspection points are still pending."
              >
                <Switch />
              </Form.Item>
            )}

            {!editingUser && (
              <Form.Item
                name="password"
                label="Password"
                rules={[
                  { required: true, message: 'Please enter password' },
                  { min: 8, message: 'Password must be at least 8 characters' },
                ]}
              >
                <Input.Password prefix={<LockOutlined />} placeholder="Password" />
              </Form.Item>
            )}
          </Form>
        </Modal>
      </Space>
    </Card>
  );
};

export default UserManagement;
